import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { useEffect, useState, useRef, useMemo } from "react";
import { ColumnDef, OrderModel } from "../../models/orders/order-list-model";
import { useTranslation } from "react-i18next";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { Dropdown } from "primereact/dropdown";
import NewOrderReview from "../New-Invoice-Report/new-order-review";
import { TabPanel, TabView } from "primereact/tabview";
import myOrderServices from "../../services/orders/myorder.service";
import { getSeverity } from "../../services/order/order-list.-mock";
import { Button } from "primereact/button";
import UserDeleteDialog from "../edit-users/user-delete-dialog";
import { Toast, ToastMessage } from "primereact/toast";
import { DataTableStateEvent } from "primereact/datatable";
import orderServices from "../../services/orders/orderservice";
import OrderPrint from "../New-Invoice-Report/new-order-print";
import ProductStorageHelpers from "../../utils/product-local-storage-wrapper";
import { ProductCheckoutModel } from "../../models/products/product-checkout-model";
import { useAppDispatch } from "../../redux/hooks";
import { itemCount } from "../../redux/slices/counter";
import ProductService from "../../services/products/products.service";
import { Card } from "primereact/card";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { Badge } from "primereact/badge";
import ProductComplaintDialog from "../New-Complaint/ProductComplaintDialog";

import CustomerProdComplaintRegister from "../Complaint/CustomerProdComplaintRegister";

type CardOrderLike = {
  orderID: number;
  orderDate: string;
  productName: string;
  filePath?: string;
  additionalProducts: number;
} & Partial<OrderModel> & { [k: string]: any };

interface OrderDetailProduct {
  productID: number;
  productName: string;
  categoryName: string;
  quantity: number;
  unitPrice: number;
  tax: number;
  filePath: string;
  productImageList: UploadedImage;
  minOrderQty: number;
  orderMultiples: number;
  weight: string;
  inStock: number;
  courierAvailable: boolean;
  isHomeDeliveryAvailable: boolean;
  measurementTypeName: string;
  id: number;
  name: string;
  description: string;
  categoryID: number;
  subCategoryID: number;
  subCategoryName: string;
  totalUnitPrice: number;
}

interface DataTableProps {
  data: OrderModel[];
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  onPageChange: (event: DataTableStateEvent) => void;
  onYearChange: (year: number) => void;
  filterYear: number;
  onOrderCancelled: () => void;
  onSearch: (keyword: string | null) => void;
  searchKeyword: string | null;
}

interface UploadedImage {
  id: number;
  serverId?: number;
  file?: File;
  mediaUrl: string;
  isDefault: boolean;
  isDelete?: boolean;
  sequenceNo?: number;
}

const MyOrderDataTable: React.FC<DataTableProps> = ({
  data,
  pageNumber,
  pageSize,
  totalRecords,
  onPageChange,
  onYearChange,
  filterYear,
  onOrderCancelled,
  onSearch,
  searchKeyword,
}) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [dialogHeader] = useState(t("orderlist_orderdetails_label"));
  const [localFilter, setLocalFilter] = useState<string>("");
  const [selectedId, setSelectedId] = useState<number>(0);
  const [notifyHeader, setNotifyHeader] = useState("");
  const [notifyMessage, setNotifyMessage] = useState("");
  const [isNotifyVisible, setIsNotifyVisible] = useState(false);
  const toast = useRef<Toast>(null);
  const currentYear = new Date().getFullYear();
  const [yearList, setYearList] = useState<number[]>([]);
  const [orderDetails, setOrderDetails] = useState<OrderModel | null>(null);
  const [printEnabled, setPrintEnabled] = useState(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [isProductSpecificView, setIsProductSpecificView] = useState(false);
  const [reorderLoading, setReorderLoading] = useState<number | null>(null);
  const dispatch = useAppDispatch();
  const firstRecordIndex = (pageNumber - 1) * pageSize;

  const [showComplaintDialog, setShowComplaintDialog] = useState(false);
  const [selectedOrderForComplaint, setSelectedOrderForComplaint] = useState<OrderModel | null>(null);

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [paymentFilter, setPaymentFilter] = useState<string | null>(null);

  // Dynamic filter options from data
  const statusOptions = useMemo(() => {
    const uniqueStatuses = Array.from(new Set(
      data.map(order => order.orderStatus?.toLowerCase().replace(/\s+/g, '')).filter(Boolean)
    ));

    return [
      { label: 'All Status', value: null },
      ...uniqueStatuses.map(status => {
        // Convert status to display label
        const displayLabel = data.find(o =>
          o.orderStatus?.toLowerCase().replace(/\s+/g, '') === status
        )?.orderStatus || status;

        return { label: displayLabel, value: status };
      })
    ];
  }, [data]);

  const paymentOptions = useMemo(() => {
    const uniquePayments = Array.from(new Set(
      data.map(order => (order as any).paymentStatus?.toLowerCase().replace(/\s+/g, '')).filter(Boolean)
    ));

    return [
      { label: 'All Payments', value: null },
      ...uniquePayments.map(payment => {
        // Convert payment status to display label
        const displayLabel = data.find(o =>
          ((o as any).paymentStatus?.toLowerCase().replace(/\s+/g, '') === payment)
        )?.paymentStatus || payment;

        return { label: displayLabel, value: payment };
      })
    ];
  }, [data]);

  // Productwise tab state
  const [productwiseData, setProductwiseData] = useState<CardOrderLike[]>([]);
  const [productwiseTotalRecords, setProductwiseTotalRecords] = useState(0);
  const [productwisePageNumber, setProductwisePageNumber] = useState<number>(pageNumber);
  const [productwisePageSize, setProductwisePageSize] = useState<number>(pageSize);
  const [productwiseLoading, setProductwiseLoading] = useState(false);
  const productwiseFirstIndex = (productwisePageNumber - 1) * productwisePageSize;


  // Productwise paginator handler
  const handleProductwisePaginator = (e: PaginatorPageChangeEvent) => {
    const nextPage = Math.floor(e.first / e.rows) + 1;
    fetchProductwise(nextPage, e.rows);
  };


  useEffect(() => {
    const savedTab = sessionStorage.getItem('myOrdersActiveTab');
    if (savedTab) setActiveTab(parseInt(savedTab));
  }, []);

  useEffect(() => {
    sessionStorage.setItem('myOrdersActiveTab', activeTab.toString());
  }, [activeTab]);

  useEffect(() => {
    const years: number[] = [];
    for (let year = 2023; year <= currentYear; year++) years.push(year);
    setYearList(years);
  }, [currentYear]);

  // Create year options with proper label/value structure
  const yearOptions = useMemo(() => {
    return yearList.map(year => ({
      label: year.toString(),
      value: year
    }));
  }, [yearList]);

  useEffect(() => {
    setLocalFilter(searchKeyword || "");
  }, [searchKeyword]);

  // Dynamic search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localFilter.trim()) {
        onSearch(localFilter);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [localFilter]);

  // Deduplicate products by keeping only the latest invoice for each product
  const deduplicateProductsByLatestInvoice = (products: CardOrderLike[]): CardOrderLike[] => {
    const productMap = new Map<string, CardOrderLike>();

    for (const product of products) {
      const productKey = `${product.productName}-${product.orderID}`;
      const existing = productMap.get(productKey);

      if (!existing) {
        productMap.set(productKey, product);
      } else {
        const currentDate = new Date((product as any).invoicedDate || (product as any).invoiceDate || 0).getTime();
        const existingDate = new Date((existing as any).invoicedDate || (existing as any).invoiceDate || 0).getTime();

        if (currentDate > existingDate) {
          productMap.set(productKey, product);
        }
      }
    }

    return Array.from(productMap.values());
  };

  // Fetch productwise data
  const fetchProductwise = async (page = productwisePageNumber, size = productwisePageSize) => {
    try {
      setProductwiseLoading(true);

      const res = await myOrderServices.getProductwiseOrders({
        Year: filterYear,
        PageNumber: page,
        PageSize: size,
        Keyword: (localFilter?.trim() || null)
      });

      const items = res?.productOrders || [];
      const total = res?.totalRecords || 0;

      const mapped: CardOrderLike[] = items.map((p: any) => ({
        ...p,
        orderID: p.orderID || p.orderId || 0,
        orderDate: p.orderDate || p.orderedOn || new Date().toISOString(),
        productName: p.productName || "",
        filePath: p.filePath || p.imageUrl || process.env.PUBLIC_URL + "/assests/images/Desiri-Groundnut-Oils.png",
        additionalProducts: 0
      }));

      const deduplicatedProducts = deduplicateProductsByLatestInvoice(mapped);

      setProductwiseData(deduplicatedProducts);
      setProductwiseTotalRecords(total);
      setProductwisePageNumber(page);
      setProductwisePageSize(size);
    } catch (err) {
      setProductwiseData([]);
      setProductwiseTotalRecords(0);
    } finally {
      setProductwiseLoading(false);
    }
  };

  // Fetch productwise data when switching tabs or filters change
  useEffect(() => {
    if (activeTab === 1) {
      fetchProductwise(1, productwisePageSize);
    }
  }, [activeTab, filterYear, localFilter]);

  const handleSetOrderData = async (rowData: { orderID: number }) => {
    setVisible(true);
    // Clear previous order details immediately to prevent showing stale data
    setOrderDetails(null);
    setIsProductSpecificView(false);
    try {
      const details = await orderServices.GetOrderByOrderId(rowData.orderID);
      // Always set the order details, whether it's data or null
      setOrderDetails(details || null);
    } catch (error) {
      // Ensure orderDetails is cleared on error as well
      setOrderDetails(null);
      showMessage("Error", "Failed to fetch order details", "error");
    }
  };

  const handleSetProductData = async (productData: CardOrderLike): Promise<void> => {
    setVisible(true);
    setIsProductSpecificView(true);
    try {
      const details = await orderServices.GetOrderByOrderId(productData.orderID);

      if (details) {

        const filterProduct = (product: any) => isProductMatch(product, productData);

        const filteredProducts = details.products?.filter(filterProduct) || [];

        const filteredDetails = {
          ...details,
          orderDetail: filteredProducts,
          orderDetails: details.orderDetails?.filter(filterProduct) || [],
          products: filteredProducts
        };

        setOrderDetails(filteredDetails);
      }
    } catch (error) {
      showMessage("Error", "Failed to fetch product details", "error");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') onSearch(localFilter || null);
  };

  // Filter data based on status and payment filters
  const filteredData = data.filter((order) => {
    // Status filter
    if (statusFilter) {
      const orderStatusLower = order.orderStatus?.toLowerCase().replace(/\s+/g, '');
      if (orderStatusLower !== statusFilter) {
        return false;
      }
    }

    // Payment filter - Use PaymentStatus from backend
    if (paymentFilter) {
      const backendPaymentStatus = (order as any).paymentStatus || '';
      const paymentStatusLower = backendPaymentStatus.toLowerCase().replace(/\s+/g, '');

      if (paymentStatusLower !== paymentFilter) {
        return false;
      }
    }

    return true;
  });

  const showMessage = (label: string, message: string, severity: ToastMessage['severity']) => {
    toast.current?.show({ severity, summary: label, detail: message, life: 3000 });
  };

  const cancelOrder = async () => {
    try {
      const response = await myOrderServices.updateOrderStatus({
        orderID: selectedId,
        orderStatusID: 6,
      });
      if (response?.isSuccess) {
        showMessage('Cancel Order', 'Order cancelled successfully', 'success');
        onOrderCancelled();
      } else {
        showMessage('Cancel Order', response?.message || 'Error in cancel order', 'error');
      }
    } catch (error) {
      showMessage('Error', 'Failed to cancel order', 'error');
    }
  };

  const handleCancelOrder = (rowData: OrderModel): void => {
    setSelectedId(rowData.orderID);
    setNotifyHeader("Cancel Order");
    setNotifyMessage("Are you sure you want to cancel this order?");
    setIsNotifyVisible(true);
  };

  const hideNotifyDialog = (value: boolean) => {
    if (value) cancelOrder();
    setIsNotifyVisible(false);
  };

  const handleTriggerPrint = (val: boolean) => {
    setPrintEnabled(val);
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-gb', { day: '2-digit', month: 'short', year: 'numeric' });

  const isProductMatch = (product: any, targetProduct: CardOrderLike) => (
    product.productID === targetProduct.productID ||
    product.productId === targetProduct.productID ||
    product.id === targetProduct.productID ||
    product.productName === targetProduct.productName ||
    product.name === targetProduct.productName
  );

  const isProductInvoiced = (orderDetails: OrderModel | null): boolean => {
    if (!orderDetails) return false;

    const orderData = orderDetails as any;

    const hasInvoices = orderData.invoices && orderData.invoices.length > 0;
    const hasOrderLevelInvoice = orderData.invoiceNo || orderData.invoicedDate;
    const hasProductLevelInvoice = (orderData.products || orderData.orderDetail || []).some((product: any) =>
      product.invoiceNo || product.invoicedDate
    );

    return hasInvoices || hasOrderLevelInvoice || hasProductLevelInvoice;
  };

  const handleProductComplaint = async (productData: CardOrderLike): Promise<void> => {
    try {
      const response = await ProductService.getReorderProductsByOrderId(productData.orderID);

      if (response && response.isSuccess === false) {
        showMessage("Error", response.message, "error");
        return;
      }

      const details = response?.data || response;
      if (!details || details.length === 0) {
        showMessage("Error", "No product details found for this order", "error");
        return;
      }

      const specificProduct = details.find((product: any) =>
        isProductMatch(product, productData)
      );

      const productDetail = transformToCartItem(specificProduct || details[0]);

      const getLatestInvoiceInfo = () => {
        const productDataAny = productData as any;

        if (productDataAny.invoices?.length) {
          const productInvoices = productDataAny.invoices.filter((invoice: any) =>
            invoice.invoiceProductName === productData.productName ||
            invoice.productName === productData.productName
          );

          if (productInvoices.length) {
            const latestInvoice = productInvoices.reduce((latest: any, current: any) => {
              const latestTime = new Date(latest.invoicedDate || latest.invoiceDate || 0).getTime();
              const currentTime = new Date(current.invoicedDate || current.invoiceDate || 0).getTime();
              return currentTime > latestTime ? current : latest;
            });

            return {
              invoiceNo: latestInvoice.invoiceNo?.toString(),
              invoicedDate: latestInvoice.invoicedDate || latestInvoice.invoiceDate
            };
          }
        }

        return {
          invoiceNo: productDataAny.invoiceNo?.toString(),
          invoicedDate: productDataAny.invoicedDate || productDataAny.orderInvoice?.[0]?.invoiceDate || ""
        };
      };

      const latestInvoiceInfo = getLatestInvoiceInfo();

      setSelectedOrderForComplaint({
        ...(productData as any),
        productID: productDetail.id,
        productId: productDetail.id,
        productName: productDetail.name,
        categoryID: productDetail.categoryID,
        categoryName: productDetail.categoryName,
        invoicedDate: latestInvoiceInfo.invoicedDate,
        customerID: (productData as any).customerID || 0,
        orderInvoice: [{
          orderInvoiceID: productData.orderID,
          invoiceNo: latestInvoiceInfo.invoiceNo,
          invoiceDate: latestInvoiceInfo.invoicedDate
        }],
        orderDetails: [{
          productID: productDetail.id,
          productName: productDetail.name,
          categoryID: productDetail.categoryID,
          categoryName: productDetail.categoryName
        }]
      } as unknown as OrderModel);
      setShowComplaintDialog(true);
    } catch (error) {
      showMessage("Error", "Failed to load product details", "error");
    }
  };

  const transformToCartItem = (product: OrderDetailProduct): ProductCheckoutModel => ({
    id: product.productID || product.id,
    name: product.productName || product.name,
    description: product.description || product.categoryName || "",
    unitPrice: product.unitPrice,
    tax: product.tax,
    weight: product.weight || "",
    minOrderQty: product.minOrderQty || 1,
    orderMultiples: product.orderMultiples || 1,
    productImageList: product.productImageList,
    categoryID: product.categoryID || 0,
    categoryName: product.categoryName || "",
    subCategoryID: product.subCategoryID || 0,
    subCategoryName: product.subCategoryName || "",
    quantity: product.quantity || 1,
    courierAvailable: product.courierAvailable || false,
    inStock: product.inStock || 0,
    minQuantity: product.minOrderQty || 1,
    isHomeDeliveryAvailable: product.isHomeDeliveryAvailable || false,
    measurementTypeName: product.measurementTypeName || ""
  });

  const handleProductReorder = async (productData: CardOrderLike): Promise<void> => {
    setReorderLoading(productData.orderID);
    try {
      const response = await ProductService.getReorderProductsByOrderId(productData.orderID);

      if (response && response.isSuccess === false) {
        showMessage("Error", response.message || "This product is currently unavailable", "error");
        return;
      }

      const details = response?.data || response;
      if (!details || details.length === 0) {
        showMessage("Error", "No order details found", "error");
        return;
      }

      const specificProduct = details.find((product: any) =>
        isProductMatch(product, productData)
      );

      if (!specificProduct) {
        showMessage("Error", response?.message, "error");
        return;
      }

      const cartItem = transformToCartItem(specificProduct);
      const existingCart = ProductStorageHelpers.GetProductStorage() || [];

      if (existingCart.some((item: ProductCheckoutModel) => item.id === cartItem.id)) {
        showMessage("Info", "This item is already in your cart", "info");
        return;
      }

      const updatedCart = [...existingCart, cartItem];
      ProductStorageHelpers.SetProductStorage(updatedCart);
      dispatch(itemCount(updatedCart.length));

      showMessage("Reorder Successful", "Item added to cart", "success");
    } catch (error) {
      showMessage("Error", "Failed to reorder product", "error");
    } finally {
      setReorderLoading(null);
    }
  };

  const handleReorder = async (rowData: { orderID: number }) => {
    setReorderLoading(rowData.orderID);
    try {
      const response = await ProductService.getReorderProductsByOrderId(rowData.orderID);

      if (response && response.isSuccess === false) {
        showMessage("Error", response.message || "This product is currently unavailable", "error");
        return;
      }

      if (response && response.isSuccess === true && response.message) {
        showMessage("Info", response.message, "info");
      }

      const details = response?.data || response;
      if (!details || details.length === 0) {
        showMessage("Error", "No order details found", "error");
        return;
      }

      const cartItems = details.map((product: OrderDetailProduct) => transformToCartItem(product));
      const existingCart = ProductStorageHelpers.GetProductStorage() || [];
      const newItems = cartItems.filter((item: ProductCheckoutModel) =>
        !existingCart.some((existing: ProductCheckoutModel) => existing.id === item.id)
      );

      if (newItems.length === 0) {
        showMessage("Info", "All items are already in your cart", "info");
        return;
      }

      const updatedCart = [...existingCart, ...newItems];
      ProductStorageHelpers.SetProductStorage(updatedCart);
      dispatch(itemCount(updatedCart.length));

      showMessage("Reorder Successful", `${newItems.length} item(s) added to cart`, "success");
    } catch (error) {
      showMessage("Error", "Failed to reorder product(s)", "error");
    } finally {
      setReorderLoading(null);
    }
  };

  const ProductCard = ({ order }: { order: CardOrderLike }) => {
    const [isHovered, setIsHovered] = useState(false);

    const isInvoiced = !!(order as any).invoiceNo && !!(order as any).invoicedDate;
    const displayDate = isInvoiced
      ? formatDate((order as any).invoicedDate)
      : formatDate(order.orderDate);

    return (
      <Card
        className="mb-3 h-full flex flex-column"
        style={{
          boxShadow: isHovered
            ? "0 8px 20px rgba(144, 238, 144, 0.7)"
            : "0 4px 12px rgba(144, 238, 144, 0.5)",
          borderRadius: "8px",
          border: "1px solid #e2f5e2",
          transform: isHovered ? "scale(1.03)" : "scale(1)",
          transition: "all 0.3s ease",
          zIndex: isHovered ? 10 : 1,
          height: "100%"
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex flex-column flex-grow-1 p-2">
          {order.additionalProducts > 0 && (
            <Badge
              value={`+${order.additionalProducts}`}
              severity="info"
              className="absolute top-0 right-0 m-1"
              style={{ fontSize: '0.8rem' }}
            />
          )}

          <div className="text-xs text-500 mb-1">
            {isInvoiced ? "DELIVERED" : "ORDER PLACED"}
          </div>
          <div className="font-bold mb-1 text-sm">
            {displayDate}
          </div>

          <div
            className="flex justify-content-center mb-1 cursor-pointer"
            onClick={() => handleSetProductData(order)}
            style={{ height: "7rem" }}
          >
            {order.filePath && (
              <div className="h-full flex align-items-center justify-content-center">
                <img
                  src={order.filePath}
                  alt={order.productName}
                  className="h-full object-contain"
                  style={{ maxWidth: "100%" }}
                />
              </div>
            )}
          </div>

          <div
            className="text-sm font-bold mb-1 cursor-pointer"
            style={{ minHeight: "2.5rem" }}
            onClick={() => handleSetProductData(order)}
          >
            <div className="line-clamp-2">{order.productName}</div>
          </div>

          <div className="flex justify-content-around mt-auto">
            <Button
              icon="pi pi-shopping-cart"
              label="Buy it again"
              onClick={() => handleProductReorder(order)}
              loading={reorderLoading === order.orderID}
              disabled={reorderLoading !== null}
              className="p-button-success text-xs"
              style={{ minWidth: "100px", padding: "0.3rem" }}
            />
            <Button
              label="View item"
              onClick={() => handleSetProductData(order)}
              className="p-button-outlined text-xs"
              style={{ minWidth: "60px", padding: "0.3rem" }}
            />

            {isInvoiced && (
              <Button
                label="Raise Query"
                className="p-button-warning text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  handleProductComplaint(order);
                }}
              />
            )}
          </div>
        </div>
      </Card>
    );
  };

  const baseColumns: ColumnDef[] = [
    {
      field: "orderID",
      header: "ORDER ID",
      style: { minWidth: "7rem" },
      cell: (row: OrderModel) => (
        <span
          style={{ color: "#1976d2", fontWeight: "600", cursor: "pointer" }}
          onClick={() => handleSetOrderData({ orderID: row.orderID })}
        >
          {row.orderID}
        </span>
      ),
    },
    {
      field: "orderDate",
      header: "DATE",
      style: { minWidth: "8rem" },
      cell: (row: OrderModel) => (
        <span style={{ color: '#424242' }}>{formatDate(row.orderDate)}</span>
      ),
    },
    {
      field: "filePath",
      header: "ITEMS",
      style: { minWidth: "15rem" },
      cell: (row: OrderModel) => (
        <div
          style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
          onClick={() => handleSetOrderData({ orderID: row.orderID })}
        >
          {row.filePath && (
            <>
              <img
                src={row.filePath}
                alt=""
                style={{
                  width: "40px",
                  height: "40px",
                  objectFit: "contain",
                  borderRadius: "4px",
                  marginRight: "12px"
                }}
              />
              <div style={{ color: '#424242' }}>
                {row.productName}
              </div>
            </>
          )}
        </div>
      ),
    },
    {
      field: "orderStatus",
      header: "STATUS",
      style: { minWidth: "10rem" },
      cell: (row: OrderModel) => {
        const orderStatusLower = row.orderStatus?.toLowerCase().replace(/\s+/g, '');
        let bgColor = '#e3f2fd';
        let textColor = '#1976d2';

        if (orderStatusLower === 'delivered') {
          bgColor = '#e8f5e9';
          textColor = '#2e7d32';
        } else if (orderStatusLower === 'processing' || orderStatusLower === 'inprogress') {
          bgColor = '#e3f2fd';
          textColor = '#1976d2';
        } else if (orderStatusLower === 'outfordelivery') {
          bgColor = '#e1f5fe';
          textColor = '#0288d1';
        } else if (orderStatusLower === 'pending' || orderStatusLower === 'orderreceived') {
          bgColor = '#e3f2fd';
          textColor = '#1976d2';
        } else if (orderStatusLower === 'cancelled') {
          bgColor = '#ffebee';
          textColor = '#c62828';
        }

        return (
          <span
            style={{
              background: bgColor,
              color: textColor,
              padding: '0.35rem 0.75rem',
              borderRadius: '12px',
              fontSize: '0.85rem',
              fontWeight: '500',
              display: 'inline-block'
            }}
          >
            {row.orderStatus}
          </span>
        );
      },
    },
    {
      field: "grossAmount",
      header: "AMOUNT",
      style: { minWidth: "8rem" },
      cell: (row: OrderModel) => (
        <div style={{ fontWeight: "600", color: '#2e7d32' }}>
          {row.grossAmount.toLocaleString("en-gb", {
            style: "currency",
            currency: "INR",
          })}
        </div>
      ),
    },
    {
      field: "paymentStatus",
      header: "PAYMENT",
      style: { minWidth: "12rem" },
      sortable: true,
      cell: (row: OrderModel) => {
        // Use PaymentStatus from backend stored procedure
        const paymentStatus = (row as any).paymentStatus || 'PaymentPending';
        let paymentText = paymentStatus;
        let bgColor = '#fff3e0';
        let textColor = '#f57c00';

        // Map PaymentStatus to display colors
        const statusLower = paymentStatus.toLowerCase().replace(/\s+/g, '');

        if (statusLower === 'paid') {
          paymentText = 'Paid';
          bgColor = '#e8f5e9';
          textColor = '#2e7d32';
        } else if (statusLower === 'partiallypaid') {
          paymentText = 'Partially paid';
          bgColor = '#e3f2fd';
          textColor = '#1976d2';
        } else if (statusLower === 'paymentpending') {
          paymentText = 'Payment Pending';
          bgColor = '#fff3e0';
          textColor = '#f57c00';
        } else if (statusLower === 'ordercancelled' || statusLower === 'cancelled') {
          paymentText = 'Order Cancelled';
          bgColor = '#ffebee';
          textColor = '#c62828';
        }

        return (
          <span
            style={{
              background: bgColor,
              color: textColor,
              padding: '0.35rem 0.75rem',
              borderRadius: '12px',
              fontSize: '0.85rem',
              fontWeight: '500',
              display: 'inline-block'
            }}
          >
            {paymentText}
          </span>
        );
      }
    },
    {
      header: "ACTION",
      style: { textAlign: "center", minWidth: "5rem" },
      cell: (row: OrderModel) =>
        row.orderStatusID === 1 && row.invoiceNo === 0 ? (
          <Button
            icon="pi pi-times-circle"
            onClick={() => handleCancelOrder(row)}
            className="p-button-rounded p-button-text p-button-danger"
            tooltip="Cancel Order"
            tooltipOptions={{ position: 'top' }}
          />
        ) : null
    }
  ];

  const reorderColumn: ColumnDef = {
    header: "BUY AGAIN",
    style: { textAlign: "center", minWidth: "7rem" },
    cell: (row: OrderModel) => (
      <Button
        icon="pi pi-shopping-cart"
        onClick={() => handleReorder({ orderID: row.orderID })}
        className="p-button-success p-button-rounded"
        tooltip="Buy It Again"
        tooltipOptions={{ position: 'top' }}
        loading={reorderLoading === row.orderID}
        disabled={reorderLoading !== null}
      />
    )
  };

  const orderwiseColumns: ColumnDef[] = [
    baseColumns[0],
    baseColumns[1],
    baseColumns[3],
    baseColumns[4],
    baseColumns[5],
    reorderColumn
  ];

  const hideDialog = () => {
    setVisible(false);
    // Clear order details when dialog is closed to prevent stale data
    setOrderDetails(null);
  };

  const renderHeader = () => {
    return (
      <div className="flex flex-column gap-3">
        {/* Outer Container - matches POS Movements card */}
        <div className="card p-4 w-full" style={{
          backgroundColor: '#e8f5e9',
          borderRadius: '12px'
        }}>
          {/* Header Section with gradient - nested inside outer container */}
          <div className="flex align-items-center gap-3 mb-4" style={{
            backgroundImage: 'linear-gradient(to right, white, rgb(232, 245, 233))',
            padding: '16px',
            borderRadius: '8px',
            marginLeft: '-16px',
            marginRight: '-16px',
            marginTop: '-16px',
            paddingLeft: '16px',
            paddingRight: '16px'
          }}>
            <div style={{
              backgroundColor: 'rgb(76, 175, 80)',
              borderRadius: '10px',
              padding: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '48px',
              height: '48px'
            }}>
              <i className="pi pi-shopping-bag" style={{ color: 'white', fontSize: '1.5rem' }}></i>
            </div>
            <div style={{ flex: 1 }}>
              <h5 className="m-0 mb-1" style={{ color: '#2d3748', fontWeight: '600', fontSize: '1.125rem' }}>
                My Order
              </h5>
              <p className="m-0" style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                Track, manage, and review your order history
              </p>
              {/* Order Count */}
              <div className="flex align-items-center gap-2 mt-2">
                <i className="pi pi-chart-bar" style={{ fontSize: '0.875rem', color: 'rgb(76, 175, 80)' }}></i>
                <span style={{ fontSize: '0.875rem', color: '#2d3748' }}>
                  <span className="font-bold">{totalRecords}</span> {t("myOrders_placedInYear")}{filterYear}
                </span>
              </div>
            </div>
          </div>

          {/* Filter Header */}
          <div className="flex align-items-center gap-2 mb-3">
            <i className="pi pi-filter" style={{ color: 'rgb(76, 175, 80)', fontSize: '1.1rem' }}></i>
            <span style={{ fontWeight: '600', color: '#2d3748', fontSize: '0.95rem' }}>Filter Orders</span>
          </div>

          {/* Filter Section */}
          <div className="flex justify-content-between align-items-center gap-3">
            <div className="flex align-items-center gap-2" style={{ flex: 1, maxWidth: '400px' }}>
              <span className="p-input-icon-left w-full">
                <i className="pi pi-search" />
                <InputText
                  type="search"
                  value={localFilter}
                  onChange={(e) => {
                    setLocalFilter(e.target.value);
                    if (e.target.value === "") onSearch(null);
                  }}
                  placeholder="Search by Order ID...."
                  className="w-full"
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db'
                  }}
                />
              </span>
            </div>

            {/* Filter Dropdowns */}
            <div className="flex align-items-center gap-2">
              <Dropdown
                options={yearOptions}
                value={filterYear}
                onChange={(e) => onYearChange(e.value)}
                placeholder="Select Year"
                className="w-9rem my-orders-dropdown"
                optionLabel="label"
                optionValue="value"
                style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db'
                }}
                panelClassName="my-orders-dropdown-panel"
              />
              <Dropdown
                value={statusFilter}
                options={statusOptions}
                onChange={(e) => setStatusFilter(e.value)}
                placeholder="All Status"
                className="w-10rem my-orders-dropdown"
                optionLabel="label"
                optionValue="value"
                showClear={statusFilter !== null}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db'
                }}
                panelClassName="my-orders-dropdown-panel"
              />
              <Dropdown
                value={paymentFilter}
                options={paymentOptions}
                onChange={(e) => setPaymentFilter(e.value)}
                placeholder="All Payments"
                className="w-10rem my-orders-dropdown"
                optionLabel="label"
                optionValue="value"
                showClear={paymentFilter !== null}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db'
                }}
                panelClassName="my-orders-dropdown-panel"
              />
            </div>
          </div>
        </div>

        {/* Custom Button Tabs */}
        <div className="flex gap-2 mt-3">
          <style>{`
            /* Fix dropdown corner gaps - comprehensive approach */
            .my-orders-dropdown.p-dropdown {
              overflow: hidden !important;
              border-radius: 8px !important;
            }
            
            .my-orders-dropdown .p-dropdown {
              overflow: hidden !important;
              border-radius: 8px !important;
            }
            
            .my-orders-dropdown .p-dropdown .p-inputtext {
              border-radius: 8px !important;
            }
            
            .my-orders-dropdown .p-dropdown .p-dropdown-trigger {
              border-radius: 0 8px 8px 0 !important;
            }
            
            .my-orders-dropdown .p-dropdown-label {
              border-radius: 8px 0 0 8px !important;
            }
            
            /* Dropdown Panel - Enhanced styling with unique panelClassName */
            .my-orders-dropdown-panel.p-dropdown-panel {
              border-radius: 12px !important;
              box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
              border: 1px solid #f0f0f0 !important;
            }
            
            /* Items container padding */
            .my-orders-dropdown-panel.p-dropdown-panel .p-dropdown-items {
              padding: 6px;
            }
            
            /* Dropdown item base styling */
            .my-orders-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item {
              border-radius: 8px !important;
              margin: 2px 8px !important;
              padding: 0.4rem 0.75rem !important;
              font-size: 0.9125rem !important;
              transition: all 0.2s ease;
            }
            
            /* Hover effect for dropdown items - GREEN BACKGROUND WITH WHITE TEXT */
            .my-orders-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item:not(.p-highlight):not(.p-disabled):hover {
              background: rgb(76, 175, 80) !important;
              color: #ffffff !important;
            }
            
            /* Selected item effect - DARKER GREEN BACKGROUND WITH WHITE TEXT */
            .my-orders-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight {
              background: #388E3C !important;
              color: #ffffff !important;
            }
          `}</style>
          <button
            onClick={() => setActiveTab(0)}
            style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              border: 'none',
              background: activeTab === 0 ? 'rgb(76, 175, 80)' : '#e0e0e0',
              color: activeTab === 0 ? 'white' : '#757575',
              fontWeight: activeTab === 0 ? '600' : '500',
              fontSize: '0.95rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: activeTab === 0 ? '0 2px 8px rgba(76, 175, 80, 0.3)' : 'none'
            }}
          >
            Orderwise
          </button>
          <button
            onClick={() => setActiveTab(1)}
            style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              border: 'none',
              background: activeTab === 1 ? 'rgb(76, 175, 80)' : '#e0e0e0',
              color: activeTab === 1 ? 'white' : '#757575',
              fontWeight: activeTab === 1 ? '600' : '500',
              fontSize: '0.95rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: activeTab === 1 ? '0 2px 8px rgba(76, 175, 80, 0.3)' : 'none'
            }}
          >
            Productwise
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <div className="col-12">
        <div className="card p-4">
          {renderHeader()}

          {activeTab === 0 ? (
            <div style={{ marginTop: '1.5rem' }}>
              <DataTable
                value={filteredData}
                paginator
                rows={pageSize}
                first={firstRecordIndex}
                totalRecords={totalRecords}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                emptyMessage="No orders found"
                stripedRows
                paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                currentPageReportTemplate="{first} to {last} of {totalRecords}"
                className="rounded-datatable"
                style={{
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: 'white',
                  border: '1px solid rgb(224, 224, 224)'
                }}
                onPage={onPageChange}
                lazy
                rowHover
              >
                {orderwiseColumns.map((col, i) => (
                  <Column
                    key={i}
                    field={col.field}
                    header={col.header}
                    style={col.style}
                    body={col.cell}
                    headerStyle={{
                      background: '#fafafa',
                      color: '#616161',
                      fontWeight: '600',
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px',
                      padding: '0.875rem 1rem',
                      borderBottom: '2px solid #e0e0e0'
                    }}
                    bodyStyle={{
                      padding: '0.875rem 1rem'
                    }}
                  />
                ))}
              </DataTable>
            </div>
          ) : (
            <div style={{ marginTop: '1.5rem' }}>
              <div className="grid">
                {productwiseLoading ? (
                  <div className="text-center p-5">Loading products...</div>
                ) : productwiseData.length > 0 ? (
                  productwiseData.map((item) => (
                    <div
                      key={`${item.orderID}-${item.productID ?? item.productName}`}
                      className="col-12 md:col-6 lg:col-3 p-1"
                    >
                      <ProductCard order={item} />
                    </div>
                  ))
                ) : (
                  <div className="text-center p-5">
                    <h3>No products found</h3>
                  </div>
                )}
              </div>

              <Paginator
                first={productwiseFirstIndex}
                rows={productwisePageSize}
                totalRecords={productwiseTotalRecords}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                onPageChange={handleProductwisePaginator}
                template="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                currentPageReportTemplate="{first} to {last} of {totalRecords}"
              />
            </div>
          )}
        </div>

        <UserDeleteDialog
          messageHeader={notifyHeader}
          notifyMessage={notifyMessage}
          isVisible={isNotifyVisible}
          isHide={hideNotifyDialog}
        />

        {orderDetails && (
          <Dialog
            header={dialogHeader}
            visible={visible}
            style={{ width: "67vw" }}
            onHide={hideDialog}
          >
            <TabView>
              <TabPanel header="Order Details" leftIcon="pi pi-file mr-1">
                <NewOrderReview
                  TriggerPrint={handleTriggerPrint}
                  PrintEnabled={printEnabled}
                  OrderData={<OrderPrint orderinfo={orderDetails} />}
                  hideTotals={isProductSpecificView}
                  hideEstimatedDelivery={isProductSpecificView && isProductInvoiced(orderDetails)}
                />
              </TabPanel>
            </TabView>
          </Dialog>
        )}

    <ProductComplaintDialog
          visible={showComplaintDialog}
          onHide={() => setShowComplaintDialog(false)}
          productData={selectedOrderForComplaint as any}
          onSuccess={() => {
            setShowComplaintDialog(false);
          }}
        />
      </div>
    </>
  );
};

export default MyOrderDataTable;